import {
  FlagCountResponse,
  ItemsForReviewRequest,
  ItemsForSummaryRequest,
} from "../api/Items";
import {
  ReportInfoCompany,
  ReportSummaryItemApi,
  SourceTypeFilter,
} from "../api/Reports";
import { getBehaviorColorCode } from "../shared-components/behaviorColors";
import { isActorUuidMatch } from "../api/Users";
import { WORKFLOW_STATUS_ENUM } from "../api/Workflows";

const defaultString = "N/A";
export interface KeyValue {
  key: string;
  value: string;
}

export interface KeyValueFilter {
  [key: string]: KeyValue[];
}
export const genericErrorLoadingMessage = "Error Loading Report Data.";

export enum ReportDetailTypes {
  PROFILE_CONFIRM = "PROFILE_CONFIRM",
  PROFILE_QA = "PROFILE_QA",
  MANUAL_RATE = "MANUAL_RATE",
  SYSTEM = "SYSTEM",
  INVALID = "INVALID",
  SUMMARY = "SUMMARY",
  AMENDMENT = "AMENDMENT",
  SCRAPE = "SCRAPE",
  WEB_CONTENT = "WEB_CONTENT",
  POST_EDIT = "POST_EDIT",
}

export const getCompanyName = (
  company?: Partial<ReportInfoCompany>
): string => {
  return company && company.name ? company.name : defaultString;
};

export const getSubCompaniesNames = (
  subcompanies: Partial<ReportInfoCompany>[] = []
): string => {
  const companies: string[] = subcompanies.map((subcompany) =>
    getCompanyName(subcompany)
  );
  return companies.join(", ");
};

export const flagBadgeClass = (
  color:
    "green" | "yellow" | "blue" | "orange" | "purple" | "pink" | "sky" | "cyan"
) => {
  switch (color) {
    case "green":
      return "bg-green-50 border-green-500 text-green-500";
    case "yellow":
      return "bg-yellow-50 border-yellow-500 text-yellow-500";
    case "blue":
      return "bg-blue-50 border-blue-500 text-blue-500";
    case "orange":
      return "bg-orange-50 border-orange-600 text-orange-600";
    case "purple":
      return "bg-purple-50 border-purple-600 text-purple-600";
    case "pink":
      return "bg-pink-50 border-pink-600 text-pink-600";
    case "sky":
      return "bg-sky-50 border-sky-600 text-sky-600";
    default:
      return "bg-cyan-50 border-cyan-600 text-cyan-600";
  }
};

export const getWebsiteName = (url: string): string => {
  const parsed = new URL(url);

  const separated = parsed.hostname.split(".");

  return separated[separated.length - 2];
};

export const newShade = (hexColor: string, magnitude: number) => {
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
};

export const getColorCode = getBehaviorColorCode;

export const reportFailed = (report: ReportSummaryItemApi): boolean => {
  return report.workflow_status === WORKFLOW_STATUS_ENUM.FAILED;
};

/** True when another actor holds the lock (matches backend augment_report_summary). */
export function isReportLockedByOther(
  report: Pick<ReportSummaryItemApi, "is_locked" | "locked_by"> | undefined,
  actorUuid: string | undefined | null
): boolean {
  if (!report?.is_locked) return false;
  return !isActorUuidMatch(actorUuid, report.locked_by);
}

/** Defer lock read-only decisions until actor identity is known (DEV-4655). */
export function isActorIdentityPendingForLock(
  isActorInfoLoading: boolean,
  locationIsReadOnly: boolean | undefined,
  report: Pick<ReportSummaryItemApi, "is_locked"> | undefined
): boolean {
  return (
    isActorInfoLoading &&
    locationIsReadOnly === undefined &&
    !!report?.is_locked
  );
}

export function countWords(text: string) {
  // Remove HTML tags first and replace them with spaces to preserve word boundaries
  let result = text.replace(/<[^>]*>/g, " ");

  // Remove emojis using a pattern similar to the Python version
  const emojiPattern =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2500}-\u{2BEF}\u{2702}-\u{27B0}\u{24C2}-\u{1F251}\u{1f926}-\u{1f937}\u{10000}-\u{10ffff}\u{2640}-\u{2642}\u{2600}-\u{2B55}\u{200d}\u{23cf}\u{23e9}\u{231a}\u{fe0f}\u{3030}]+/gu;
  result = result.replace(emojiPattern, " ");

  // Remove special characters except spaces
  result = result.replace(/[^a-zA-Z0-9\s]/g, "");

  // Keep a single space between words
  result = result.replace(/\s+/g, " ").trim();

  // Split by space and count the words
  return result.length === 0 ? 0 : result.split(" ").length;
}

/** Deep-clone flag counts before local adjustments so React Query cache is not mutated. */
export function cloneFlagCountResponse(
  raw: FlagCountResponse
): FlagCountResponse {
  return JSON.parse(JSON.stringify(raw)) as FlagCountResponse;
}

export type BehaviorFlagMapperInput = {
  useMapper: boolean;
  mapper: { [key: string]: string };
};

export function processFlagCountForDisplay(
  raw: FlagCountResponse,
  behaviorMapping: BehaviorFlagMapperInput
): {
  processed: FlagCountResponse;
  flagAmounts: { [key: string]: number };
} {
  const processed = cloneFlagCountResponse(raw);
  const newFlags: { [key: string]: number } = {};

  let newGroup = processed?.behaviors?.groups?.filter((behavior) => {
    if (behavior.name === "ADDED") {
      processed.behaviors.total -= behavior.count;
      return false;
    }
    newFlags[behavior.name] = behavior.count;
    return true;
  });

  if (behaviorMapping.useMapper) {
    const mapperKeys = Object.keys(behaviorMapping.mapper);
    mapperKeys.forEach((key) => {
      const upperKey = key.toUpperCase();
      if (behaviorMapping.mapper[upperKey]) {
        const total = newFlags[key] ?? 0;
        const mapped = newFlags[behaviorMapping.mapper[upperKey]] ?? 0;
        if (mapped + total > 0) {
          newFlags[behaviorMapping.mapper[upperKey]] = mapped + total;
        }
      }
      delete newFlags[key];
    });
  }

  if (processed?.behaviors?.groups) processed.behaviors.groups = newGroup;

  return { processed, flagAmounts: newFlags };
}

export function postAndArticleCountsFromFlagCount(
  flagCount: FlagCountResponse | undefined | null
): { postCount: number; articleCount: number; total: number } {
  const postCount =
    flagCount?.item_types.groups.find((g) => g.name === "social_media_post")
      ?.count ?? 0;
  const articleCount =
    flagCount?.item_types.groups.find((g) => g.name === "web_content")?.count ??
    0;
  return { postCount, articleCount, total: postCount + articleCount };
}

export function buildReportItemsFilterRequests(args: {
  report: ReportSummaryItemApi;
  itemTypeFilter: string[];
  behaviorFilter: string[];
  keywordFilter: string[];
  topicFilter?: string[];
  sortId: string;
  pageSize: number;
  summaryPageNumber: number;
  sourceTypeFilter: SourceTypeFilter;
  postTypeFilter: { original: boolean; shared: boolean; liked: boolean };
  dateFilter: { fromDate: string; toDate: string };
}): { review: ItemsForReviewRequest; summary: ItemsForSummaryRequest } {
  const {
    report,
    itemTypeFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter = [],
    sortId,
    pageSize,
    summaryPageNumber,
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
  } = args;

  let review: ItemsForReviewRequest = {
    report_uuid: report.report_uuid,
    item_type_name: itemTypeFilter?.length ? itemTypeFilter : [],
    behaviors: behaviorFilter,
    keywords: keywordFilter,
    topics: topicFilter,
    page_size: pageSize,
    sort_criteria: sortId,
  };

  let summary: ItemsForSummaryRequest = {
    report_id: report.report_uuid,
    item_types: itemTypeFilter?.length ? itemTypeFilter : [],
    behaviors: behaviorFilter,
    keywords: keywordFilter,
    topics: topicFilter,
    page_size: pageSize,
    page_number: summaryPageNumber,
    sort: sortId,
  };

  const sourceTypeSources: string[] = Object.keys(
    sourceTypeFilter ?? {}
  ).filter(
    (source) => sourceTypeFilter[source as keyof typeof sourceTypeFilter]
  );
  if (sourceTypeSources.length > 0) {
    review = { ...review, item_source_name: sourceTypeSources };
    summary = { ...summary, sources: sourceTypeSources };
  }

  const postTypeSources: string[] = Object.keys(postTypeFilter ?? {}).filter(
    (source) => postTypeFilter[source as keyof typeof postTypeFilter]
  );
  if (postTypeSources.length > 0) {
    review = { ...review, item_post_type: postTypeSources };
    summary = { ...summary, item_subtypes: postTypeSources };
  }

  const fromDate = dateFilter?.fromDate;
  if (fromDate) {
    review = { ...review, min_date: fromDate };
    summary = { ...summary, min_date: fromDate };
  }

  const toDate = dateFilter?.toDate;
  if (toDate) {
    review = { ...review, max_date: toDate };
    summary = { ...summary, max_date: toDate };
  }

  return { review, summary };
}

export const INSIGHT_COLORS = {
  adverse: "#020F3B",
  community: "#46B276",
  keywords: "#FF9800",
  topics: "#FACC15",
  articles: "#EC407A",
  diligence: "#a855f7",
  news: "#60a5fa",
  legal: "#1e40af",
} as const;
